import React, { useEffect, useRef, useState } from 'react';
import './zones.scss';

import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import {
  getPTZ, getResponseError, responseLogWithNotification, useUpdatePeriod,
} from '../../../utils/utils';
import {
  deletePosition, deleteZone, loadPositionByCameraList, loadZonesByCameraList, savePosition, saveZone,
} from '../actions';
import { getSystemData } from '../../System/reducer';
import Loader from '../../../components/base/Loader/Loader';
import { getZonesData } from '../reducer';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import Camera from '../../Dashboard/components/Camera';
import { calculateCamSize } from '../../Dashboard/components/CameraBlock';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import {
  Dialog, DialogContent, DialogFooter, DialogTitle,
} from '../../../components/base/Dialog/Dialog';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import { showGlobalConfirm } from '../../System/actions';
import Switches from '../../../components/fields/Switches/Switches';
import ZoneForm from './ZoneForm';
import { getCamerasData, getSettingsData } from '../../Settings/reducer';
import CameraPositionFrom from './CameraPositionFrom';
import CameraZoneFrom from './CameraZoneFrom';
import {
  deleteUser, loadPresets, setPTZ, updateCamera,
} from '../../Settings/actions';
import notificationDecorator, {
  errorNotification,
  successNotification,
} from '../../Notifications/notificationDecorator';
import { addressFormatter } from '../../../utils/formatters';

export default function ZonesPage() {
  const dispatch = useDispatch();
  const [currentTimeOut, setCurrentTimeOut] = useState(null);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [selectedPositionId, setSelectedPositionId] = useState(null);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const blockRef = useRef(null);
  const wrapperRef = useRef(null);
  const cameraBlock = useRef(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [isShowForm, setIsShowForm] = useState(false);
  const [renameId, setRenameId] = useState(null);
  const [editZone, setEditZone] = useState(false);
  const [editPosition, setEditPosition] = useState(false);
  const [polygon, setPolygon] = useState([]);
  const { updatePeriod } = useSelector(getSystemData);
  const { cameraList = [] } = useSelector(getCamerasData);
  const intl = useIntl();
  const { formatMessage } = intl;
  const {
    loadingPresetsList,
    presetsList,
  } = useSelector(getSettingsData);
  const {
    loadingPositionByCamera,
    positionByCameraList,
    loadingZonesByCamera,
    zonesByCameraList,
    loadingCreatePosition,
    loadingDeletePosition,
    loadingCreateZone,
    loadingDeleteZone,
  } = useSelector(getZonesData);

  const k1 = 0.4315; // соотношение размера страницы к размеру блока по дизайну // 0.482
  const [[width, height], setSize] = useState([window.innerWidth, window.innerHeight]);
  const fullscreenWidth = wrapperRef && wrapperRef.current && wrapperRef.current.clientWidth ? wrapperRef.current.clientWidth : width;
  const fullscreenHeight = wrapperRef && wrapperRef.current && wrapperRef.current.clientHeight ? wrapperRef.current.clientHeight : height;

  const blockWidth = blockRef && blockRef.current && blockRef.current.clientWidth ? blockRef.current.clientWidth : 473;
  const blockHeight = blockRef && blockRef.current && blockRef.current.clientHeight ? blockRef.current.clientHeight : 473;
  const frameWidth = fullscreen ? fullscreenWidth : blockWidth;
  const frameHeight = fullscreen ? fullscreenHeight : blockHeight;
  const dw = width > 1366 ? 32 : 24;
  const dh = width > 1366 ? 32 : 24;
  const { width: cameraWidth, height: cameraHeight } = calculateCamSize(frameWidth - dw, frameHeight - dh, 0);
  const selectedPosition = positionByCameraList.find(item => item.id === selectedPositionId);
  const selectedZone = zonesByCameraList.find(item => item.id === selectedZoneId);

  const loadData = () => {
    const promises = [];
    if (selectedCameraId) {
      promises.push(dispatch(loadPositionByCameraList(selectedCameraId)).catch(responseLogWithNotification));
    }
    if (selectedPositionId) {
      promises.push(dispatch(loadZonesByCameraList(selectedPositionId)).catch(responseLogWithNotification));
      promises.push(dispatch(loadPresets()).catch(responseLogWithNotification));
    }
    return Promise.all(promises);
  };
  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod, selectedCameraId, selectedPositionId]);

  useEffect(() => {
    const resizeHandler = () => setSize([window.innerWidth, window.innerHeight]);
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);

  useEffect(() => {
    if (selectedZone) {
      const p = Array.isArray(selectedZone.border) ? selectedZone.border : [];
      setPolygon(p);
    } else {
      setPolygon([]);
    }
  }, [selectedZone]);
  const selectCamera = id => setSelectedCameraId(id);

  const setCamEdit = isEdit => {
    setFullscreen(isEdit);
    if (!isEdit) {
      setEditPosition(false);
      setEditZone(false);
    }
  };

  const handleSelectPosition = item => () => {
    setSelectedPositionId(item.id);
    setSelectedZoneId(null);
    const pos = positionByCameraList.find(p => p.id === item.id);
    const ptz = getPTZ(pos);
    const camera = cameraList.find(c => c.id === selectedCameraId);
    dispatch(setPTZ({ values: ptz, id: selectedCameraId }))
      .then(() => {
        dispatch(updateCamera({ ...camera, ...ptz }));
      })
      .catch(response => errorNotification(formatMessage({ id: 'camera.save.ptz' }), getResponseError(response)));
  };

  const handleCreatePosition = () => {
    setSelectedPositionId(null);
    setEditPosition(true);
    setEditZone(false);
    setPolygon([]);
    return window.handleEditCam && window.handleEditCam();
  };

  const handleEditPosition = item => () => {
    setSelectedPositionId(item.id);
    setEditPosition(true);
    setEditZone(false);
    setSelectedZoneId(null);
    return window.handleEditCam && window.handleEditCam();
  };

  const handleSelectZone = item => () => {
    setSelectedZoneId(item.id);
    setEditPosition(false);
    setEditZone(false);
    setIsShowForm(false);
    setPolygon([]);
  };

  const handleSetZone = () => {
    setEditPosition(false);
    setEditZone(true);
    return window.handleEditCam && window.handleEditCam();
  };

  const handleCreateZone = () => {
    if (!selectedPositionId) {
      return;
    }
    setSelectedZoneId(null);
    setEditPosition(false);
    setEditZone(true);
    setIsShowForm(true);
  };
  const handleEditZone = item => event => {
    setSelectedZoneId(item.id);
    setIsShowForm(true);
    event.preventDefault();
    event.stopPropagation();
    return false;
  };
  const handleDeleteZone = item => event => {
    dispatch(showGlobalConfirm({
      confirmMessage: formatMessage({ id: 'app.delete.message' }, { name: item.name }),
      confirmButtonText: formatMessage({ id: 'app.delete' }),
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deleteZone(item.id)).then(() => {
        loadData();
        successNotification(formatMessage({ id: 'zone.delete.title' }), formatMessage({ id: 'zone.delete.success' }));
      }).catch(response => {
        errorNotification(formatMessage({ id: 'zone.delete.title' }), getResponseError(response));
      });
    }).catch(() => {});

    event.preventDefault();
    event.stopPropagation();
    return false;
  };
  const handleDeletePosition = item => event => {
    dispatch(showGlobalConfirm({
      confirmMessage: formatMessage({ id: 'app.delete.message' }, { name: item.name }),
      confirmButtonText: formatMessage({ id: 'app.delete' }),
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deletePosition(item.id)).then(() => {
        loadData();
        successNotification(formatMessage({ id: 'position.delete.title' }), formatMessage({ id: 'position.delete.success' }));
      }).catch(response => {
        errorNotification(formatMessage({ id: 'position.delete.title' }), getResponseError(response));
      });
    }).catch(() => {});

    event.preventDefault();
    event.stopPropagation();
    return false;
  };
  const handleActivation = item => event => {
    const title = !item.active ? formatMessage({ id: 'camera.position.activate.process' }) : formatMessage({ id: 'camera.position.deactivate.process' });
    dispatch(savePosition({ id: item.id, data: { ...item, active: !item.active } }))
      .then(() => {
        loadData();
        successNotification(title);
      }).catch(response => { errorNotification(title, getResponseError(response)); });
    event.preventDefault();
    event.stopPropagation();
    return false;
  };
  const handleSavePosition = () => {};
  const cancelZoneForm = () => { setIsShowForm(false); };
  const selectZonePolygon = p => setPolygon(p);
  const ignoreZone = e => {
    dispatch(saveZone({
      id: selectedZone.id,
      data: { ...selectedZone, active: e.target.value },
    })).then(() => {
      loadData();
      successNotification(formatMessage({ id: 'zone.save' }), formatMessage({ id: 'zone.save.success' }));
    }).catch(response => {
      errorNotification(formatMessage({ id: 'zone.save' }), getResponseError(response));
    });
  };

  const moreFormatter = item => (
    <TooltipWithPortal
      showByClick
      content={<Icons customIcon icon="dot" className="more-vert" />}
      classNamePortal="more-actions"
      tooltipContent={(
        <div className="more-wrapper">
          <Button variant="link" className="w-100 parking-meter-more-button" onClick={handleEditPosition(item)}>
            <Icons width={22} height={22} customIcon icon="edit-camera" className="button-icons" />
            {formatMessage({ id: 'camera.position.edit' })}
          </Button>
          <Button variant="link" className="w-100 parking-meter-more-button" onClick={handleActivation(item)}>
            <Icons width={22} height={22} customIcon icon="play-3" className="button-icons" />
            {formatMessage({ id: item && item.active ? 'camera.position.activate' : 'camera.position.deactivate' })}
          </Button>
          <Button variant="link" className="w-100 parking-meter-more-button" onClick={handleDeletePosition(item)}>
            <Icons width={22} height={22} customIcon icon="trash" className="button-icons" />
            {formatMessage({ id: 'camera.position.delete' })}
          </Button>
        </div>
        )}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );

  const renderRenameDialog = () => (
    <Dialog
      className={classNames('zone-rename-dialog-container')}
      onClose={() => { setRenameId(null); }}
      open
    >
      <DialogTitle header={formatMessage({ id: 'position.rename' })} title={<Icons onClick={() => { setRenameId(null); }} customIcon icon="close" className="close" />} />
      <DialogContent className="hff-content">
        <TextInputWithLabel label={formatMessage({ id: 'position.name' })} variant="material" placeholder={formatMessage({ id: 'position.name.placeholder' })} />
      </DialogContent>
      <DialogFooter>
        <Button onClick={handleSavePosition}>{formatMessage({ id: 'app.save' })}</Button>
        <Button variant="secondary" onClick={() => { setRenameId(null); }}>{formatMessage({ id: 'app.close' })}</Button>
      </DialogFooter>
    </Dialog>
  );

  return (
    <div className="page zones-page">
      <Loader loading={loadingPresetsList || loadingPositionByCamera || loadingZonesByCamera || loadingCreatePosition || loadingDeletePosition || loadingCreateZone || loadingDeleteZone} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.zones' })}</div>
        <div className="buttons" />
      </div>
      <div className="page-content-wrapper">
        <div className="page-content">
          <div className="settings-wrapper zones-settings-wrapper" ref={wrapperRef}>
            <div className="settings-menu frame">
              <div className="settings-list-wrapper position-column-wrapper">
                <div className="settings-list custom-scroll-theme position-column" ref={blockRef}>
                  <div
                    className="cameras-block"
                    ref={cameraBlock}
                  >
                    <Camera
                      cameraIndex={0}
                      cameraList={cameraList}
                      cameraWidth={cameraWidth}
                      cameraHeight={cameraHeight}
                      frameWidth={frameWidth}
                      frameHeight={frameHeight}
                      selectCamera={selectCamera}
                      setCamEdit={setCamEdit}
                      selectedCameraId={selectedCameraId}
                      cameraBlock={cameraBlock}
                      fwd={0}
                      fhd={-16}
                      anchor="left"
                      customComponent={editPosition ? CameraPositionFrom : editZone ? CameraZoneFrom : null}
                      position={selectedPosition}
                      loadData={loadData}
                      selectZonePolygon={selectZonePolygon}
                      polygon={polygon}
                    />
                  </div>
                  <div className="settings-header">
                    <div className="settings-label">{formatMessage({ id: 'position' })}</div>
                    <div className="settings-buttons">
                      <Button disabled={!selectedCameraId} variant="secondary" onClick={handleCreatePosition}>
                        <Icons
                          customIcon
                          icon="plus"
                        /> {formatMessage({ id: 'app.create' })}
                      </Button>
                    </div>
                  </div>
                  <div className="settings-list-wrapper">
                    <div className="settings-list custom-scroll-theme">
                      {selectedCameraId && positionByCameraList.map((item, key) => (
                        <div key={key} onClick={handleSelectPosition(item)} className={classNames('position-menu-item', { selected: selectedPositionId === item.id })}>
                          <div className="position-menu-number">{item.number}</div>
                          <div className="position-menu-name"><TooltipWithPortal classNamePortal="portal-name-tooltip" classNameDropdown="name-tooltip" content={item.name || ''} /></div>
                          <div className="position-menu-more">
                            {moreFormatter(item)}
                          </div>
                        </div>
                      ))}
                      {!positionByCameraList.length ? (
                        <div className="empty-list">
                          <div className="empty-list-text">{formatMessage({ id: 'position.add' })}</div>
                        </div>
                      ) : null}

                      {renameId ? renderRenameDialog() : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="settings-category frame">
              <div className="settings-header">
                <div className="settings-label">{formatMessage({ id: 'zones' })}</div>
                <div className="settings-buttons">
                  <Button variant="secondary" onClick={handleCreateZone} disabled={!selectedPositionId}><Icons
                    customIcon
                    icon="plus"
                  /> {formatMessage({ id: 'app.create' })}
                  </Button>
                </div>
              </div>
              <div className="settings-list-wrapper">
                <div className="settings-list custom-scroll-theme">
                  {zonesByCameraList.map((item, key) => (
                    <div
                      key={key}
                      onClick={handleSelectZone(item)}
                      className={classNames('settings-menu-item', { selected: item.id === selectedZoneId })}
                    >{item.name || ''}
                      <div className="edit-and-delete-buttons">
                        <div className="menu-item-button" onClick={handleEditZone(item)}><Icons
                          customIcon
                          icon="pencil"
                          width={18}
                          height={18}
                        />
                        </div>
                        <div className="menu-item-button" onClick={handleDeleteZone(item)}><Icons
                          customIcon
                          icon="trash"
                          width={18}
                          height={18}
                        />
                        </div>
                      </div>
                    </div>
                  ))}
                  {!zonesByCameraList.length ? (
                    <div className="empty-list">
                      <div className="empty-list-text">{formatMessage({ id: 'zones.not.found' })}</div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="settings-options frame">
              <div className="settings-header">
                <div className="settings-label">{formatMessage({ id: 'zone.settings' })}</div>
                <div className="settings-buttons" />
              </div>
              {!selectedZone && !isShowForm ? (
                <div className="empty-list">
                  <div className="empty-list-text">{formatMessage({ id: 'zones.add' })}</div>
                </div>
              ) : null}
              {isShowForm ? <ZoneForm key={selectedZoneId} loadData={loadData} zone={selectedZone} position={selectedPosition} cancel={cancelZoneForm} handleSetZone={handleSetZone} polygon={polygon} /> : null}
              {selectedZone && !isShowForm
                ? (
                  <>
                    <div className="settings-form">
                      <div className="settings-view-form zone-option custom-scroll-theme">
                        <div className="settings-view-row">
                          <div className="settings-view-label">{formatMessage({ id: 'zone.name' })}:</div>
                          <div className="settings-view-value">
                            <div className="zone-switches">
                              <div>{selectedZone.name || ''}</div>
                              <div className="switches-wrapper">
                                <Switches withLabel label={formatMessage({ id: 'zone.ignore' })} inputProps={{ value: selectedZone.ignore, onChange: ignoreZone }} />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="settings-view-row">
                          <div className="settings-view-label">{formatMessage({ id: 'address' })}:</div>
                          <div className="settings-view-value">{addressFormatter(selectedZone) || ''}</div>
                        </div>
                        <div className="settings-view-row">
                          <div className="settings-view-label">{formatMessage({ id: 'zone.fixation' })}:</div>
                          <div className="settings-view-value">{selectedZone.save_all_fixation ? formatMessage({ id: 'zone.saved_all_fixation' }) : formatMessage({ id: 'zone.saved_not_all_fixation' })}</div>
                        </div>
                        <div className="settings-view-row">
                          <div className="settings-view-label">{formatMessage({ id: 'presets.violation' })}:</div>
                        </div>
                        <div className="settings-view-row presets-list-wrapper">
                          <div className="settings-view-value">
                            <ul className="presets-list">
                              {(selectedZone.presets || []).map((presetId, key) => {
                                const preset = presetsList.find(p => p.id === presetId);
                                return preset && (<li key={key} className="presets-list-item">{preset.name || ''}</li>);
                              })}

                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="settings-footer">
                      <Button className="clear-show" onClick={handleEditZone(selectedZone)}>{formatMessage({ id: 'app.edit' })}</Button>
                      <Button className="clear-data" variant="secondary" onClick={handleDeleteZone(selectedZone)}>{formatMessage({ id: 'app.delete' })}</Button>
                    </div>
                  </>
                ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
